import moment from 'moment';
import { IFile } from 'models/general';
import { CertificationFormData, TypeOption, Certificate } from '../types';

/**
 * Transforms certification details from API response to form data for editing
 */
export const transformCertificationForEdit = (
  certificationDetails: any,
  projectId: string | null,
  typeOptions: TypeOption[],
): CertificationFormData => {
  // Find the correct option from typeOptions using the certification type ID
  const selectedOption = typeOptions.find(option => option.value.id === certificationDetails.certification.id);

  return {
    type: selectedOption || '',
    certificationTypeId: certificationDetails.certification.id,
    certificationId: certificationDetails.idNumber || '',
    description: certificationDetails.description || '',
    issueDate: certificationDetails.completionDate ? moment.utc(certificationDetails.completionDate).toDate() : null,
    expirationDate: certificationDetails.expirationDate
      ? moment.utc(certificationDetails.expirationDate).toDate()
      : null,
    images: transformFilesToImages((certificationDetails.files || []).filter((f: any) => !f.isDeleted)),
  };
};

/**
 * Transforms file objects from API to image format for the form
 */
export const transformFilesToImages = (files: any[]): (IFile & { isExisting?: boolean })[] => {
  return files.map(file => ({
    id: file.id,
    url: file.url,
    image: file.url,
    fileName: file.fileName || file.displayName,
    // AddImages detects the format from `displayName` when a `url` is present (as it is for
    // loaded files). Populate it so PDFs render as PDF previews, matching the Government ID section.
    displayName: file.displayName || file.fileName,
    isExisting: true,
  }));
};

/**
 * Gets the display name for a certification based on project context
 */
export const getCertificationDisplayName = (certification: Certificate, projectId: string | null): string => {
  if (!projectId || !certification.aliasesInProjects?.length) {
    return certification.certification.name;
  }

  const projectAlias = certification.aliasesInProjects.find(alias => alias.project.id === projectId);

  return projectAlias?.alias || certification.certification.name;
};

/**
 * Validates if a certification type requires expiration date
 */
export const isExpirationDateRequired = (certificationType: any): boolean => {
  return (
    certificationType &&
    typeof certificationType === 'object' &&
    'value' in certificationType &&
    certificationType.value?.isExpirationDateMandatory === true
  );
};

/**
 * Prepares form data for API submission
 */
export const prepareFormDataForSubmission = (
  formData: CertificationFormData,
  deletedFileIds: string[] = [],
): CertificationFormData => ({
  ...formData,
  deletedFileIds,
});
